import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { FaTimes, FaNetworkWired } from 'react-icons/fa';

const DeviceAssignmentModal = ({ vehicle, onClose, onAssignmentUpdated }) => {
  const [devices, setDevices] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(vehicle?.assignedDevice?.id || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  useEffect(() => {
    fetchDevices();
    fetchVehicles();
  }, []);

  const fetchDevices = async () => {
    setLoading(true);
    setError('');
    try {
      const devicesRef = collection(db, 'devices');
      const snapshot = await getDocs(devicesRef);
      const devicesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setDevices(devicesList);
    } catch (error) {
      console.error('Error fetching devices:', error);
      setError('Failed to fetch devices');
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      const vehiclesRef = collection(db, 'vehicles');
      const snapshot = await getDocs(vehiclesRef);
      const vehiclesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVehicles(vehiclesList);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  // Check if device is linked to another vehicle
  const getDeviceLinkedVehicle = (deviceId) => {
    if (!deviceId) return null;
    return vehicles.find(v => 
      v.assignedDevice && 
      v.assignedDevice.id === deviceId && 
      v.id !== vehicle.id
    );
  };

  // Handle device selection change
  const handleDeviceChange = (deviceId) => {
    setSelectedDevice(deviceId);
    setWarning('');
    
    if (deviceId) {
      const linkedVehicle = getDeviceLinkedVehicle(deviceId);
      if (linkedVehicle) {
        setWarning(`This device is already linked to ${linkedVehicle.registrationNumber}. Linking it here will unlink it from that vehicle.`);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const selectedDeviceData = devices.find(d => d.id === selectedDevice);

      // Check if device is linked to another vehicle and unlink it first
      if (selectedDeviceData) {
        const linkedVehicle = getDeviceLinkedVehicle(selectedDevice);
        if (linkedVehicle) {
          // Unlink device from previous vehicle
          const previousVehicleRef = doc(db, 'vehicles', linkedVehicle.id);
          await updateDoc(previousVehicleRef, {
            assignedDevice: null
          });
          
          // Also update the device document
          const previousDeviceRef = doc(db, 'devices', selectedDevice);
          await updateDoc(previousDeviceRef, {
            vehicleId: null,
            vehicleRegistrationNumber: null
          });
        }
      }

      const vehicleRef = doc(db, 'vehicles', vehicle.id);
      await updateDoc(vehicleRef, {
        assignedDevice: selectedDeviceData ? {
          id: selectedDeviceData.id,
          deviceName: selectedDeviceData.deviceName,
          imeiNumber: selectedDeviceData.imeiNumber,
          status: selectedDeviceData.status
        } : null,
        lastDeviceAssignmentUpdate: new Date().toISOString()
      });

      // Also update the device to link it to this vehicle
      if (selectedDeviceData) {
        const deviceRef = doc(db, 'devices', selectedDeviceData.id);
        await updateDoc(deviceRef, {
          vehicleId: vehicle.id,
          vehicleRegistrationNumber: vehicle.registrationNumber
        });
      }

      // Unlink previous device if a different device is selected
      if (vehicle.assignedDevice && vehicle.assignedDevice.id !== selectedDevice) {
        const previousDeviceRef = doc(db, 'devices', vehicle.assignedDevice.id);
        await updateDoc(previousDeviceRef, {
          vehicleId: null,
          vehicleRegistrationNumber: null
        });
      }

      if (onAssignmentUpdated) {
        onAssignmentUpdated();
      }
      onClose();
    } catch (error) {
      console.error('Error updating device assignment:', error);
      setError('Failed to update device assignment');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
          <div className="text-center">Loading devices...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-black">Link Device - {vehicle?.registrationNumber}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl" aria-label="Close modal">
              <FaTimes />
            </button>
          </div>
          {error && (
            <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg mb-6">{error}</div>
          )}
          {warning && (
            <div className="bg-yellow-500/10 border border-yellow-500 text-yellow-700 px-4 py-3 rounded-lg mb-6">{warning}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaNetworkWired className="inline mr-2" />Device
              </label>
              <select 
                value={selectedDevice} 
                onChange={(e) => handleDeviceChange(e.target.value)} 
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
              >
                <option value="">Select Device (or leave unlinked)</option>
                {devices.map((device) => {
                  const linkedVehicle = getDeviceLinkedVehicle(device.id);
                  const isLinkedToOther = linkedVehicle && linkedVehicle.id !== vehicle.id;
                  return (
                    <option key={device.id} value={device.id}>
                      {device.deviceName || 'Unknown Device'} - IMEI: {device.imeiNumber || 'N/A'} - {device.status || 'active'}
                      {isLinkedToOther ? ` (Linked to ${linkedVehicle.registrationNumber})` : ''}
                    </option>
                  );
                })}
              </select>
              {selectedDevice && (
                <div className="mt-2 p-3 bg-gray-50 rounded">
                  {(() => {
                    const device = devices.find(d => d.id === selectedDevice);
                    return device ? (
                      <div className="text-sm text-gray-600">
                        <p className="font-semibold text-gray-800 mb-2">{device.deviceName || 'Unknown Device'}</p>
                        <p className="mb-1"><span className="font-medium">IMEI:</span> {device.imeiNumber || 'N/A'}</p>
                        <p className="mb-1"><span className="font-medium">Location:</span> {device.location || 'Unknown'}</p>
                        <p className="mb-1">
                          <span className="font-medium">Status:</span>{' '}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(device.status)}`}>
                            {device.status ? device.status.charAt(0).toUpperCase() + device.status.slice(1) : 'Active'}
                          </span>
                        </p>
                        {device.apiProvider && (
                          <p className="mb-1"><span className="font-medium">API Provider:</span> {device.apiProvider.toUpperCase()}</p>
                        )}
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-4 mt-6">
              <button 
                type="button" 
                onClick={onClose} 
                className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="flex items-center space-x-2 px-6 py-3 rounded-lg bg-[#00E0E0] text-white font-semibold hover:bg-[#00B8B8] transition-colors"
              >
                <FaNetworkWired />
                <span>Save Device Link</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DeviceAssignmentModal;

